import DesignTokens from '@tableau/design-tokens';
import { FolderSwapIcon } from '@tableau/icons/FolderSwapIcon';
import { ActivitySpinnerSize, ActivitySpinnerWidget, ButtonType, ButtonWidget, Tooltip } from '@tableau/widgets';
import React from 'react';

import { sanitize } from '~commonInfra/utils/Sanitize';
import { Messages } from '~src/codegen/Localize';
import { COLLECTION_DESCRIPTION_MAX_LENGTH } from '~src/spacesPlatform/components/content/contentDetailDialog/DescriptionDetailSection';
import { ModifiedDateMetadata, OwnerMetadata } from '~src/spacesPlatform/components/header/HeaderMetadataRow';
import { InlineEditableDescription } from '~src/spacesPlatform/components/header/InlineEditableDescription';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { Subspace, TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';
import { classes } from '~src/style';
import { IUserInfo } from '~src/userManagement/users/models/IUserInfo';

import { IContentRelayPlanDetail } from '../serverApi/types/ISelectionQuery';
import { setRelayPlanDescription } from '../utils/relayPlanActions';
import { ContentRelayPlanActionsMenu } from './ContentRelayPlanActionsMenu';
import { ContentRelayPlanContentsTab } from './ContentRelayPlanContentsTab';
import { ContentRelayPlanDetailsStyles as Styles } from './ContentRelayPlanDetailsStyles';

export const ContentRelayPlanDetailsSpaceTestIds = {
  Spinner: 'relay-plan-details-spinner',
  Error: 'relay-plan-details-error',
  BackToList: 'relay-plan-details-back-to-list',
  Title: 'relay-plan-details-title',
  RunNow: 'relay-plan-details-run-now',
};

export interface ContentRelayPlanDetailsSpaceProps {
  readonly isLoading: boolean;
  readonly hasError: boolean;
  /** The loaded plan; null while loading or on error. */
  readonly plan: IContentRelayPlanDetail | null;
  readonly owner: IUserInfo | null;
  readonly urls: SpacesSiteUrls;
  /** Re-reads the plan after an edit on the page (rename, description, contents). */
  readonly onPlanChanged: () => void;
  readonly onPlanDeleted: () => void;
  readonly onReturnToList: () => void;
}

/**
 * Presentational shell for the Content Relay plan detail page. The masthead follows the platform
 * compact header (icon, title, ⋯ menu, Owner / Modified, inline description) with Run Now on the right,
 * matching the mockup; the body is a TabbedSpace so Run History (W-23846273) can add its tab later.
 */
export function ContentRelayPlanDetailsSpace(props: ContentRelayPlanDetailsSpaceProps): JSX.Element {
  if (props.isLoading) {
    return (
      <div className={SpaceContainerStyles.SpaceContainer}>
        <div className={Styles.CenteredState}>
          <ActivitySpinnerWidget testId={ContentRelayPlanDetailsSpaceTestIds.Spinner} spinnerSize={ActivitySpinnerSize.Large} />
        </div>
      </div>
    );
  }

  const plan = props.plan;
  if (props.hasError || !plan) {
    return (
      <div className={SpaceContainerStyles.SpaceContainer}>
        <div className={Styles.CenteredState} data-tb-test-id={ContentRelayPlanDetailsSpaceTestIds.Error}>
          <div>{Messages.relayPlanDetailsLoadError()}</div>
          <ButtonWidget
            buttonType={ButtonType.Primary}
            onClick={props.onReturnToList}
            testId={ContentRelayPlanDetailsSpaceTestIds.BackToList}
          >
            {Messages.relayPlanDetailsBackToList()}
          </ButtonWidget>
        </div>
      </div>
    );
  }

  const updateDescription = function(description: string): Promise<void> {
    return setRelayPlanDescription(plan.luid, description).then(function(result) {
      if (result.errors && result.errors.length) {
        return Promise.reject(result);
      }
      props.onPlanChanged();
      return undefined;
    });
  };

  const modifiedAt = plan.updatedAt ? new Date(plan.updatedAt) : undefined;
  const masthead = (
    <div className={classes(SpaceHeaderStyles.RowV2, SpaceHeaderStyles.CompactHeader, Styles.MastheadRow)}>
      <div className={classes(SpaceHeaderStyles.RowNowrap, Styles.MastheadMain)}>
        <div className={SpaceHeaderStyles.IconDivWrapperV2}>
          <div className={Styles.MastheadIcon}>
            <FolderSwapIcon size={24} color={DesignTokens.color.font.default.secondary} alt={''} />
          </div>
        </div>
        <div className={SpaceHeaderStyles.CapWidth}>
          <div className={SpaceHeaderStyles.RowV2}>
            <h1 className={SpaceHeaderStyles.EditableTitleReadonly} title={plan.name} data-tb-test-id={ContentRelayPlanDetailsSpaceTestIds.Title}>
              {plan.name}
            </h1>
            <div className={classes(SpaceHeaderStyles.RowNowrap, SpaceHeaderStyles.EditableHeaderStylesV2)}>
              <section className={SpaceHeaderStyles.DetailIcons}>
                <div className={SpaceHeaderStyles.EditableNameRow}>
                  <ContentRelayPlanActionsMenu
                    luid={plan.luid}
                    name={plan.name}
                    onRenamed={props.onPlanChanged}
                    onDeleted={props.onPlanDeleted}
                  />
                </div>
              </section>
              <section className={SpaceHeaderStyles.MetadataV2} data-tb-test-id={'space-header-metadata'}>
                {props.owner && OwnerMetadata(props.owner, props.urls)}
                {modifiedAt && ModifiedDateMetadata(modifiedAt, !!props.owner)}
              </section>
            </div>
          </div>
          {/* Keyed by luid so the editor's local copy resets when navigating between plans. */}
          <InlineEditableDescription
            key={plan.luid}
            item={{ description: plan.description || '' }}
            isAdminOrOwner={true}
            canEdit={true}
            maxCharacterCount={COLLECTION_DESCRIPTION_MAX_LENGTH}
            descriptionPrompt={Messages.relayPlanDetailsDescriptionPrompt()}
            ariaLabel={Messages.relayPlanDetailsDescriptionAriaLabel()}
            sanitizer={sanitize}
            updateDescription={updateDescription}
          />
        </div>
      </div>
      <div className={Styles.MastheadActions}>
        {/* Running a plan ships with W-23846264; until then Run Now is shown but disabled. */}
        <Tooltip getContent={function() { return <span>{Messages.relayPlanDetailsRunNowUnavailable()}</span>; }}>
          <span>
            <ButtonWidget
              buttonType={ButtonType.Outline}
              disabled={true}
              style={{ fontWeight: 'normal' }}
              testId={ContentRelayPlanDetailsSpaceTestIds.RunNow}
            >
              {Messages.runNow()}
            </ButtonWidget>
          </span>
        </Tooltip>
      </div>
    </div>
  );

  const subspaces: ReadonlyArray<Subspace> = [
    {
      name: 'overview',
      tabLabel: Messages.overview(),
      shouldRenderTabContentCount: false,
      renderContent: function() {
        return <ContentRelayPlanContentsTab plan={plan} urls={props.urls} onPlanChanged={props.onPlanChanged} />;
      },
    },
  ];

  return (
    <TabbedSpace
      subspaces={subspaces}
      activeSubspaceName={'overview'}
      urlForSubspace={function() { return props.urls.contentRelayPlanUrl(plan.luid); }}
      mastheadElement={masthead}
      testIdPrefix={'relay-plan-details'}
      spaceItemName={plan.name}
    />
  );
}
